// Intercom Types & Interface ===========================================================
// all the Interface stuff from: https://developers.intercom.com/installing-intercom/web/attributes-objects/

export interface IntercomSettingsProperties {
    // Messenger Attributes
    // These attributes will modify the behaviour of the messenger. They do not modify user/lead data.

    app_id: string; // The app_id of your Intercom app which will indicate where to store any data
    custom_launcher_selector?: string; //The CSS selector of an element to trigger Intercom("show") in order to activate the messenger (https://docs.intercom.com/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/customize-the-intercom-messenger-technical). To target an element by ID: "#id_of_element". To target elements by class ".classname_of_elements"
    alignment?: string; //Dictate the alignment of the default launcher icon to be on the left/right. Possible values: "left" or "right" (any other value is treated as right). (https://docs.intercom.com/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/customize-the-intercom-messenger-technical)
    vertical_padding?: number; //Move the default launcher icon vertically. Padding from bottom of screen. Minimum value: 20. Does not work on mobile. (https://docs.intercom.com/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/customize-the-intercom-messenger-technical)
    horizontal_padding?: number; //Move the default launcher icon horizontally. Padding from right side of screen Minimum value: 20. Does not work on mobile. (https://docs.intercom.com/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/customize-the-intercom-messenger-technical)
    hide_default_launcher?: boolean; //Hide the default launcher icon. Setting to false will forcefully show the launcher icon (https://docs.intercom.com/configure-intercom-for-your-product-or-site/customize-the-intercom-messenger/turn-off-show-or-hide-the-intercom-messenger)
    session_duration?: number; //Time in milliseconds for the Intercom session to be considered active.A value of 5 * 60 * 1000 would set the expiry time to be 5 minutes
    action_color?: string; //Used in button links and more to highlight and emphasise
    background_color?: string; //Used behind your team profile and other attributes

    // Data Attributes
    // These attributes are used to update user/lead information.
    // When user_id / email is provided, it will be saved as a User record
    // - When no user_id / email is provided, it will be considered a Visitor record which is not seen in the Intercom dashboard. When a Visitor sends a message via the Intercom messenger they will be converted to a Lead which is viewable in the Intercom dashboard
    // - Any other attribute not listed below and not in the Messenger Attributes will be treated as a custom user attribute
    // - If the value of a custom user attribute is set to an empty string, or a string with the value "undefined", or "null", this will appear as Unknown in Platform.
    // - If a value is set for a custom user attribute that has been configured to prevent updates via the Messenger then this value will be ignored in the request.

    email?: string; //The email address of the currently logged in user (Only applicable to users)
    user_id?: string; //The user_id address of the currently logged in user (Only applicable to users)
    created_at?: number; //The Unix timestamp (in seconds) when the user signed up to your app (Only applicable to users)
    name?: string; //Name of the current user/lead
    phone?: string; //Phone number of the current user/lead
    last_request_at?: number; //This value can't actually be set by the Javascript API (it automatically uses the time of the last request but is a this is a reserved attribute)
    unsubscribed_from_emails?: boolean; //Sets the [unsubscribe status]((https://docs.intercom.com/faqs-and-troubleshooting/unsubscribing-users/how-do-i-unsubscribe-users-from-receiving-emails) of the record
    language_override?: string; //Set the messenger language programmatically (instead of relying on browser language settings)
    utm_campaign?: string; //UTM Campaign valueNote: All UTM parameters are updated automatically and can not be manually overidden
    utm_content?: string; //UTM Content value
    utm_medium?: string; //UTM Medium value
    utm_source?: string; //UTM Source value
    utm_term?: string; //UTM Term value
    avatar?: avatar; //Set the avatar/profile image associated to the current record (typically gathered via social profiles via email address)
    user_hash?: string; //Used for identity verification (Only applicable to users)
    company?: company; //Current user's company (Only applicable to users) For field definitions see Company Object in the section below Note: Company ID and company name are the minimum requirements to pass a company into Intercom.
    companies?: company[]; //An array of companies the user is associated to(Only applicable to users)
    page_title?: string; //Used for keeping track of user page views. Default value is the document title property.

    // Custom attributes
    [custom_attribute: string]: company | company[] | avatar | CustomAttribute;
}

// Avatar Object
export interface avatar {
    type?: string;
    image_url?: string;
}

// Company Object
export interface company {
    company_id?: string | undefined;
    name?: string | undefined;
    remote_created_at?: number | undefined;
    plan?: string | undefined;
    monthly_spend?: number | undefined;
    people?: number | undefined;
    size?: number | undefined;
    website?: string | undefined;
    industry?: string | undefined;
    [custom_attribute: string]: CustomAttribute;
}

type CustomAttribute = string | number | boolean | null | undefined;

export type IntercomProviderProps = {
    appId: string;
    triggerBoot?: boolean;
};

export type ProviderValueProps = {
    initIntercomInstance: () => void;
    boot: () => void;
    shutdown: () => void;
    show: () => void;
    hide: () => void;
};

// Got this from: https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/intercom-web/index.d.ts
interface IntercomCommandSignature {
    boot: (settings: IntercomSettingsProperties) => void;
    shutdown: () => void;
    update: (settings?: IntercomSettingsProperties) => void;
    hide: () => void;
    show: () => void;
    showMessages: () => void;
    showNewMessage: (prepopulateMessage?: string) => void;
    onHide: (callback: () => void) => void;
    onShow: (callback: () => void) => void;
    onUnreadCountChange: (callback: (unreadCount: number) => void) => void;
    onActivatorClick: (callback: () => void) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trackEvent: (tag?: string, metadata?: any) => void;
    getVisitorId: () => string;
    startTour: (tourId: number) => void;
    showArticle: (articleId: number) => void;
    startSurvey: (surveyId: number) => void;
    reattach_activator: () => void;
    showSpace: (space: string) => void;
}

type IntercomCommand = keyof IntercomCommandSignature;

export interface IntercomProperties {
    <Command extends IntercomCommand>(
        command: Command,
        ...params: Parameters<IntercomCommandSignature[Command]>
    ): ReturnType<IntercomCommandSignature[Command]>;
    booted: boolean;
}
